import React, { useEffect, useState } from 'react';
import { FileText, AlertCircle, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import useAppStore from '../store/appStore';

const RegulationViewer = ({ regulation, showMultiple = false, regulations = [], fullWidth = false }) => {
  const [activeProvision, setActiveProvision] = useState(null);
  
  // Get provisions from store
  const provisionsAnalysis = useAppStore(state => state.provisionsAnalysis);
  const currentProvisions = provisionsAnalysis[regulation?.id] || [];

  // Log component mount and updates
  useEffect(() => {
    console.log('%c📜 [RegulationViewer] Component updated:', 'color: #4CAF50; font-weight: bold', {
      hasRegulation: !!regulation,
      regulationId: regulation?.id,
      regulationNumber: regulation?.number,
      showMultiple,
      regulationCount: regulations.length,
      textLength: regulation?.text?.length || 0,
      provisionCount: currentProvisions.length
    });
  }, [regulation, regulations, showMultiple, currentProvisions]);

  // Module: Create legend with provision counts
  const createLegend = (provisions, regulation) => {
    // Check if this is a three-bucket classification (Treasury)
    const isThreeBucket = provisions.some(p => p.isThreeBucket);
    
    if (isThreeBucket) {
      // Count provisions for three-bucket system
      const counts = {
        mandatory: 0,
        authorized: 0,
        unauthorized: 0
      };

      provisions.forEach(prov => {
        switch (prov.authLevel) {
          case 'MANDATORY':
            counts.mandatory++;
            break;
          case 'AUTHORIZED':
            counts.authorized++;
            break;
          case 'UNAUTHORIZED':
            counts.unauthorized++;
            break;
        }
      });

      return (
        <div style={{
          padding: '12px 16px',
          background: 'linear-gradient(to right, #f8f9fa, #ffffff)',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
            fontSize: '13px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '2px'
              }}></span>
              <span style={{ color: '#155724', fontWeight: '500' }}>
                Mandatory ({counts.mandatory})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                background: '#fff3cd',
                border: '1px solid #ffeeba',
                borderRadius: '2px'
              }}></span>
              <span style={{ color: '#856404', fontWeight: '500' }}>
                Authorized ({counts.authorized})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                background: '#ffeeee',
                border: '1px solid #ffcdd2',
                borderRadius: '2px'
              }}></span>
              <span style={{ color: '#c62828', fontWeight: '500' }}>
                Unauthorized ({counts.unauthorized})
              </span>
            </div>
            {regulation?.discretionary_requirements_total > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '2px'
                }}></span>
                <span style={{ color: '#dc2626', fontWeight: '500' }}>
                  Discretionary Requirements ({regulation.discretionary_requirements_total})
                </span>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      // Count provisions for four-bucket system
      const counts = {
        express: 0,
        general: 0,
        overreach: 0,
        unauthorized: 0
      };

      provisions.forEach(prov => {
        switch (prov.authLevel) {
          case 'Express Delegation':
            counts.express++;
            break;
          case 'General Delegation':
            counts.general++;
            break;
          case 'Potential Overreach':
            counts.overreach++;
            break;
          case 'Unauthorized':
            counts.unauthorized++;
            break;
          default:
            counts.general++;
        }
      });

      return (
        <div style={{
          padding: '12px 16px',
          background: 'linear-gradient(to right, #f8f9fa, #ffffff)',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
            fontSize: '13px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '2px'
              }}></span>
              <span style={{ color: '#155724', fontWeight: '500' }}>
                Express ({counts.express})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                background: '#fff3cd',
                border: '1px solid #ffeeba',
                borderRadius: '2px'
              }}></span>
              <span style={{ color: '#856404', fontWeight: '500' }}>
                General/Partial ({counts.general})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                background: '#e1bee7',
                border: '1px solid #ce93d8',
                borderRadius: '2px'
              }}></span>
              <span style={{ color: '#6a1b9a', fontWeight: '500' }}>
                Potential Overreach ({counts.overreach})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                background: '#ffeeee',
                border: '1px solid #ffcdd2',
                borderRadius: '2px'
              }}></span>
              <span style={{ color: '#c62828', fontWeight: '500' }}>
                Unauthorized ({counts.unauthorized})
              </span>
            </div>
          </div>
        </div>
      );
    }
  };

  // Get background color for provision auth level
  const getBackgroundColor = (provision) => {
    if (!provision) return 'transparent';
    
    // Handle three-bucket system (Treasury)
    if (provision.isThreeBucket) {
      switch (provision.authLevel) {
        case 'MANDATORY':
          return '#d4edda'; // Green
        case 'AUTHORIZED':
          return '#fff3cd'; // Yellow
        case 'UNAUTHORIZED':
          return '#ffeeee'; // Red
        default:
          return 'transparent';
      }
    }
    
    // Handle four-bucket system (all other agencies)
    switch (provision.authLevel) {
      case 'Express Delegation':
        return '#d4edda';
      case 'General Delegation':
        return '#fff3cd';
      case 'Potential Overreach':
        return '#e1bee7';
      case 'Unauthorized':
        return '#ffeeee';
      default:
        return '#fff3cd';
    }
  };

  // Process text for statute and VAC links
  const processTextForLinks = (text) => {
    if (!text) return text;
    
    // Process federal statute links - format: § 16-824 or just 16-824
    let processed = text.replace(/§?\s*(\d+)-(\d+[a-z]?)/g, (match, title, section) => {
      // Use DC Council Code URL format for federal statutes
      const url = `https://code.dccouncil.gov/us/dc/council/code/sections/${title}-${section}`;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; font-weight: 500;">${match}</a>`;
    });
    
    // Process VAC links
    processed = processed.replace(/(\d+VAC[\d-]+)/g, (match) => {
      const parts = match.match(/(\d+)VAC([\d-]+)/);
      if (parts) {
        const title = parts[1];
        const rest = parts[2];
        const sections = rest.split('-');
        const url = `https://law.lis.virginia.gov/admincode/title${title}/agency${sections[0]}/chapter${sections[1]}/section${sections[2]}/`;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; font-weight: 500;">${match}</a>`;
      }
      return match;
    });
    
    return processed;
  };

  // Create VAC link for regulation number
  const createVACLink = (regNumber) => {
    if (!regNumber) return regNumber;
    
    // Handle CFR format (e.g., "18_cfr_9" -> "CFR 18.9")
    if (regNumber.includes('_cfr_')) {
      return regNumber.replace(/(\d+)_cfr_(\d+)/, 'CFR $1.$2');
    }
    
    // Handle VAC format
    const match = regNumber.match(/(\d+)VAC([\d-]+)/);
    if (!match) return regNumber;
    
    const title = match[1];
    const rest = match[2];
    const sections = rest.split('-');
    const url = `https://law.lis.virginia.gov/admincode/title${title}/agency${sections[0]}/chapter${sections[1]}/section${sections[2]}/`;
    
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#2563eb', textDecoration: 'underline' }}
      >
        {regNumber}
      </a>
    );
  };

  // Create statute link
  const createStatuteLink = (statute) => {
    if (!statute) return statute;
    
    // Handle federal statute format (e.g., "§ 16-824" or "16-824" or "§§ 16-801 and 16-825h")
    // For multiple statutes, just link the first one
    const match = statute.match(/§*\s*(\d+)-(\d+[a-z]?)/);
    if (!match) return statute;
    
    const title = match[1];
    const section = match[2];
    
    // Use DC Council Code URL format for federal statutes
    const url = `https://code.dccouncil.gov/us/dc/council/code/sections/${title}-${section}`;
    
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#2563eb', textDecoration: 'underline' }}
      >
        {statute}
      </a>
    );
  };

  // Main render function for regulation text
  const renderRegulationText = (text, regulationId, regulationUrl) => {
    if (!text) return null;

    const provisions = provisionsAnalysis[regulationId] || [];
    
    // If no provisions data, just render with basic link processing
    if (provisions.length === 0) {
      const processedText = processTextForLinks(text);
      return (
        <div 
          className="regulation-text-container" 
          style={{ 
            fontFamily: 'Times New Roman, serif', 
            fontSize: '16px', 
            lineHeight: '1.8',
            color: '#212529'
          }}
          dangerouslySetInnerHTML={{ __html: processedText }}
        />
      );
    }

    // USE THE INTELLIGENT PROVISIONS FROM THE BACKEND
    // The backend gives us properly parsed provisions - just render them in order with superscripts
    const elements = [];
    
    provisions.forEach((provision, index) => {
      if (!provision.text) return;
      
      // Process text for links
      const processedText = processTextForLinks(provision.text);
      const backgroundColor = getBackgroundColor(provision);
      
      // Add the highlighted provision text
      elements.push(
        <span
          key={`provision-text-${index}`}
          style={{
            backgroundColor,
            padding: backgroundColor !== 'transparent' ? '2px 4px' : '0',
            borderRadius: backgroundColor !== 'transparent' ? '3px' : '0',
            display: 'inline'
          }}
          dangerouslySetInnerHTML={{ __html: processedText }}
        />
      );
      
      // Add the superscript number at the END of this provision
      elements.push(
        <sup key={`provision-sup-${index}`}>
          <a 
            href={`#provision-${regulationId}-${index}`}
            style={{
              color: '#2563eb',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
            onClick={(e) => {
              e.preventDefault();
              console.log('%c🔍 [RegulationViewer] Provision link clicked:', 'color: #9C27B0', {
                provisionId: `provision-${regulationId}-${index}`,
                provisionNumber: index + 1
              });
              setActiveProvision(`provision-${regulationId}-${index}`);
            }}
          >
              {index + 1}
            </a>
          </sup>
        );
      
      // Add space after superscript
      elements.push(' ');
      });
    
    return (
      <div 
        className="regulation-text-container" 
        style={{ 
          fontFamily: 'Times New Roman, serif', 
          fontSize: '16px', 
          lineHeight: '1.8',
          color: '#212529'
        }}
      >
        {elements}
      </div>
    );
  };

  if (!regulation && !showMultiple) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>No regulation selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full overflow-y-auto ${fullWidth ? '' : 'p-6'}`}>
      {showMultiple && regulations.length > 0 ? (
        <div className="space-y-8">
          {regulations.map((reg, index) => {
            const regProvisions = provisionsAnalysis[reg.id] || [];
            return (
              <motion.div
                key={reg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border-b border-gray-200 pb-8 last:border-b-0"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-playfair font-semibold text-gray-900">
                    {createVACLink(reg.number)} - {reg.title}
                  </h3>
                  {reg.authorizingStatute && (
                    <p className="text-sm text-gray-600 mt-1">
                      Authorizing Statute: {createStatuteLink(reg.authorizingStatute)}
                    </p>
                  )}
                </div>
                {regProvisions.length > 0 && createLegend(regProvisions, reg)}
                <div className="prose max-w-none">
                  {reg.text ? (
                    renderRegulationText(reg.text, reg.id, reg.regulationUrl)
                  ) : (
                    <div className="text-gray-500 italic p-4">
                      No regulation text available
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div>
          {/* Authorizing Statute - styled nicely at top */}
          {regulation.authorizingStatute && (
            <div style={{
              padding: '14px 18px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '10px',
              marginBottom: '20px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  AUTHORIZING STATUTE
                </span>
                <span style={{
                  background: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {createStatuteLink(regulation.authorizingStatute)}
                </span>
              </div>
            </div>
          )}

          {/* Legend with provision counts */}
          {currentProvisions.length > 0 && createLegend(currentProvisions, regulation)}

          <div className="prose max-w-none">
            {regulation.text ? (
              renderRegulationText(regulation.text, regulation.id, regulation.regulationUrl)
            ) : (
              <div className="text-gray-500 italic p-4">
                No regulation text available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegulationViewer;